import React, { useState } from "react";
import axios from "axios";
import { Formik, Form, useFormikContext } from "formik";
import dayjs from "dayjs";
import * as Yup from "yup";

import { Visible } from "../../../core/components";
import { FieldGroup } from "../../../components";
import { Button, ButtonGroup, Modal } from "../../../components/ui";
import { useCreateProductPrice } from "../../../product/hooks";
import { SupplierWizardModal } from "../../../views/Users";
import {
  boxOptions,
  conditionOptions,
  locationOptions,
  monthOptions,
  yearOptions,
  sourceOptions,
} from "../../../product/constants";
import { MarketplaceActions } from "../../../order/atoms";
import { isEmpty } from "lodash";

const initialValues = {
  currency: "gbp",
  price: "",
  price_date: new Date(),
  supplier_id: "",
  condition: "new",
  box: "watch-box-papers",
  year: dayjs().year(),
  month: "00",
  source: "trade",
  location: "uk",
};
const validationSchema = Yup.object().shape({
  price: Yup.string().required("Price is required."),
  supplier_id: Yup.number().required("Supplier is required."),
  price_date: Yup.date()
    .typeError("Invalid date")
    .required("Date is required"),
});

const AddProductPriceForm = ({
  productId,
  onCancel,
  onDone,
  item,
  fieldClassName = "col-12 col-sm-6",
  setCheckUnderPrice,
  // "grid": the quote workspace — labels above, one grid, Save price / Cancel,
  // and the confirm step in place. Anything else is the original layout.
  layout = "legacy",
}) => {
  const grid = layout === "grid";
  const { createProductPrice } = useCreateProductPrice(productId);
  const [error, setError] = useState(null);
  const [isCreatingNewSupplier, setIsCreatingNewSupplier] = useState(false);
  const [defaultOptions, setDefaultOptions] = useState([]);
  const [value, setValue] = useState(null);
  const [isSendingPrice, setIsSendingPrice] = useState(false);
  const [priceData, setPriceData] = useState();
  const [pendingResetForm, setPendingResetForm] = useState(null);

  const handleSubmit = async (data, { resetForm }) => {
    setError(null);

    const priceData = {
      ...data,
      condition: [data.condition],
      year_month: `${data.year} ${
        data.month === "00" ? "" : data.month
      }`.trim(),
      price: Math.round(parseFloat(data.price) * 100),
      price_date: dayjs(data.price_date).format("YYYY-MM-DD"),
    };

    setPriceData(priceData);
    setIsSendingPrice(true);
    setPendingResetForm(() => resetForm);
  };

  const sendPrice = async (priceData) => {
    setIsSendingPrice(false);
    let response;
    try {
      response = await createProductPrice(priceData);
    } catch (error) {
      setError("The price could not be saved");
      return;
    }

    // The price is saved from here on. The underpriced check is a follow-up:
    // its failure used to show "The price could not be saved" too, and saving
    // again made a duplicate price.
    try {
      // Same-origin proxy on the Laravel backend — see useTradeImage.js
      // for rationale (production CORS block on duckdns).
      const checkUnderPriceResponse = await axios.post(
        "/api/integrations/daowhatsapp/check-underpriced",
        {
          id: response.id,
          product_id: productId,
          price: priceData.price * 100,
          currency: priceData.currency,
          price_date: priceData.price_date,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const checkUnderPrice = checkUnderPriceResponse?.data;
      if (!isEmpty(checkUnderPrice)) {
        setCheckUnderPrice && setCheckUnderPrice(checkUnderPrice);
        await axios.put(`/api/products/${checkUnderPrice.id}/update-price`, {
          trade_median: checkUnderPrice.tradeMedian,
          price_diff: checkUnderPrice.priceDiff,
          under_priced: checkUnderPrice.underpriced,
        });
      }
    } catch (error) {
      console.warn(`Price #${response?.id} saved; underpriced check failed`, error);
    }

    if (!onDone) {
      if (pendingResetForm) {
        pendingResetForm();
        setPendingResetForm(null);
      }
    } else {
      onDone();
    }
  };

  // Create a wrapper component that has access to Formik context
  const FormContent = ({ setFieldValue }) => {
    const onAfterSubmit = (value) => {
      if (value) {
        const newOption = {
          id: value.id,
          name: value.first_name + " " + value.last_name,
        };
        setDefaultOptions([newOption]);
        setValue(value.id);
        // Update the Formik field value to ensure it's included in form submission
        setFieldValue("supplier_id", value.id);
      }
      setIsCreatingNewSupplier(false);
    };

    if (grid) {
      const select = (name, label, options) => (
        <div className="qws-field">
          <FieldGroup name={name} type="select" options={options} label={label} placeholder="Select…" />
        </div>
      );
      return (
        <>
          <Form className="qws-add-price">
            <div className="qws-form-grid">
              <div className="qws-field qws-field--span2">
                <FieldGroup
                  name="supplier_id"
                  type="userSearchSelect"
                  label="Supplier"
                  placeholder="Search suppliers…"
                  value={value}
                  defaultOptions={defaultOptions}
                />
              </div>
              {select("source", "Source", sourceOptions)}
              {select("location", "Country", locationOptions)}
              {select("currency", "Currency", [
                { id: "gbp", name: "GBP" },
                { id: "eur", name: "EUR" },
                { id: "usd", name: "USD" },
                { id: "hkd", name: "HKD" },
                { id: "usdt", name: "USDT" },
              ])}
              <div className="qws-field">
                <FieldGroup name="price" type="numberFormat" label="Price" placeholder="0" />
              </div>
              <div className="qws-field">
                <FieldGroup name="price_date" type="date" label="Price date" />
              </div>
              {select("condition", "Condition", conditionOptions)}
              {select("box", "Box & papers", boxOptions)}
              {select("year", "Year", yearOptions)}
              {select("month", "Month", monthOptions)}
            </div>

            <Visible when={error}>
              <div className="error">{error}</div>
            </Visible>

            {isSendingPrice ? (
              <div className="qws-confirm" role="group" aria-label="Confirm the price">
                <span>Save this price to the price history?</span>
                <Button variant="ghost" data-btn="price-edit" onClick={() => setIsSendingPrice(false)}>
                  Edit
                </Button>
                <Button variant="primary" data-btn="price-confirm" onClick={() => sendPrice(priceData)}>
                  Confirm
                </Button>
              </div>
            ) : (
              <div className="qws-actions-row">
                <Button variant="ghost" data-btn="supplier-new" onClick={() => setIsCreatingNewSupplier(true)}>
                  + New supplier
                </Button>
                {onCancel && (
                  <Button variant="secondary" data-btn="price-cancel" onClick={onCancel}>
                    Cancel
                  </Button>
                )}
                <Button type="submit" variant="primary" data-btn="price-create">
                  Save price
                </Button>
              </div>
            )}
          </Form>

          <SupplierWizardModal
            isOpen={isCreatingNewSupplier}
            onClose={() => setIsCreatingNewSupplier(false)}
            afterSubmit={onAfterSubmit}
            submitLabel="Save"
          />
        </>
      );
    }

    return (
      <>
        <Form>
          <div className="row">
            <div className={fieldClassName}>
              <FieldGroup
                name="supplier_id"
                type="userSearchSelect"
                placeholder="Supplier"
                value={value}
                defaultOptions={defaultOptions}
              />
            </div>
            <div className={fieldClassName}>
              <Button
                variant="secondary"
                fullWidth
                data-btn="supplier-new"
                onClick={() => setIsCreatingNewSupplier(true)}
              >
                New supplier
              </Button>
            </div>
          </div>
          <div className="row">
            <div className={fieldClassName}>
              <FieldGroup
                name="currency"
                type="select"
                options={[
                  { id: "gbp", name: "GBP" },
                  { id: "eur", name: "EURO" },
                  { id: "usd", name: "DOLLARS" },
                  { id: "hkd", name: "HKD" },
                  { id: "usdt", name: "CRYPTO-USDT" },
                ]}
                placeholder="Currency"
              />
            </div>
            <div className={fieldClassName}>
              <FieldGroup
                name="price"
                type="numberFormat"
                placeholder="Price"
              />
            </div>
            <div className={fieldClassName}>
              <FieldGroup
                name="price_date"
                type="date"
                placeholder="Price date"
              />
            </div>
            <div className={fieldClassName}>
              <FieldGroup
                name="condition"
                type="select"
                options={conditionOptions}
                placeholder="Condition"
              />
            </div>
            <div className={fieldClassName}>
              <FieldGroup
                name="box"
                type="select"
                options={boxOptions}
                placeholder="Box"
              />
            </div>
            {window.innerWidth > 1200 && <div className={fieldClassName}></div>}
            <div className={fieldClassName}>
              <FieldGroup
                name="year"
                type="select"
                options={yearOptions}
                placeholder="Year"
              />
            </div>
            <div className={fieldClassName}>
              <FieldGroup
                name="month"
                type="select"
                options={monthOptions}
                placeholder="Month"
              />
            </div>
            <div className={fieldClassName}>
              <FieldGroup
                name="source"
                type="select"
                options={sourceOptions}
                placeholder="Source"
              />
            </div>
            <div className={fieldClassName}>
              <FieldGroup
                name="location"
                type="select"
                options={locationOptions}
                placeholder="Country"
              />
            </div>
            <div className={fieldClassName}>
              <Button type="submit" variant="primary" fullWidth data-btn="price-create">
                Send price
              </Button>
            </div>
            {/* Collapses the form back to the "Add Price" button. Only rendered
                where the form is expandable (product preview — AddProductPrice
                owns the open/closed mode); the Quote pricing tab renders it
                permanently open and passes no `onCancel`, so it stays hidden
                there. Shares the grid cell footprint of Send Price, so it sits
                beside it on wide panels and stacks below it on mobile. */}
            {onCancel && (
              <div className={fieldClassName}>
                <Button variant="ghost" fullWidth data-btn="price-cancel" onClick={onCancel}>
                  Close
                </Button>
              </div>
            )}
          </div>

          <Visible when={error}>
            <div className="error">{error}</div>
          </Visible>
        </Form>

        <SupplierWizardModal
          isOpen={isCreatingNewSupplier}
          onClose={() => setIsCreatingNewSupplier(false)}
          afterSubmit={onAfterSubmit}
          submitLabel="Save"
        />
      </>
    );
  };

  return (
    <div className="add-product-price-form">
      <Formik
        {...{
          initialValues,
          validationSchema,
          onSubmit: handleSubmit,
        }}
      >
        {(props) => <FormContent {...props} />}
      </Formik>

      {/* The grid layout (quote workspace) confirms in place instead. */}
      <Modal
        open={isSendingPrice && !grid}
        onClose={() => setIsSendingPrice(false)}
        variant="dialog"
        size="sm"
        title="Confirmation needed"
        footer={
          <ButtonGroup align="end">
            <Button
              variant="secondary"
              data-btn="price-edit"
              onClick={() => {
                setIsSendingPrice(false);
              }}
            >
              Edit
            </Button>
            <Button
              variant="primary"
              data-btn="price-confirm"
              onClick={() => {
                sendPrice(priceData);
              }}
            >
              Confirm
            </Button>
          </ButtonGroup>
        }
      />
    </div>
  );
};

export default AddProductPriceForm;
